import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { assetUrl } from '../lib/api';
import { palette as TOKENS, alpha, type } from '../design';
import { Avatar } from './Avatar';

// ── Terracotta Studio Design Tokens ──────────────────────────────────────────


// ── SVG Decorations ──────────────────────────────────────────────────────────

export function AsanohaBackground({ opacity = 0.035, color = TOKENS.caramelDeep, children, style = {} }) {
  return (
    <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity, pointerEvents: 'none', zIndex: 0 }}>
        <defs>
          <pattern id="asanoha" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M24 0 L48 24 L24 48 L0 24 Z" fill="none" stroke={color} strokeWidth="0.6" />
            <path d="M24 0 L24 48 M0 24 L48 24" fill="none" stroke={color} strokeWidth="0.6" />
            <path d="M0 0 L48 48 M48 0 L0 48" fill="none" stroke={color} strokeWidth="0.6" />
            <circle cx="24" cy="24" r="1.5" fill={color} opacity="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#asanoha)" />
      </svg>
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ── Snackbar ──────────────────────────────────────────────────────────────────

export { Snackbar } from './Snackbar';
export { SnackbarContainer } from './SnackbarContainer';

// ── Compatibility stubs (will be removed as pages are rebuilt) ───────────────

export function GridBackground({ children, className = '', style = {} }) {
  return <div className={className} style={{ background: TOKENS.ivory, ...style }}>{children}</div>;
}

export function CherryBlossom() { return null; }
export function Sparkle() { return null; }
export function WashiTape() { return null; }

// ── Stars ─────────────────────────────────────────────────────────────────────

export function Stars({ rating, size = 14 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ fontSize: size, color: i <= Math.floor(rating) ? TOKENS.terracotta : TOKENS.border }}>★</span>
      ))}
    </span>
  );
}

// ── Pill Badge ────────────────────────────────────────────────────────────────

export function Badge({ children, variant = 'terracotta' }) {
  const styles = {
    terracotta: { background: alpha('terracotta', 'tint'), color: TOKENS.terracottaDeep, border: '1px solid ' + alpha('terracotta', 'selection') },
    sage: { background: alpha('sageGreen', 'tint'), color: TOKENS.sageDark, border: '1px solid ' + alpha('sageGreen', 'selection') },
    gold: { background: alpha('caramelGold', 'selection'), color: TOKENS.walnut, border: '1px solid ' + alpha('caramelGold', 'scrim') },
    blue: { background: alpha('mutedBlue', 'tint'), color: TOKENS.mutedBlue, border: '1px solid ' + alpha('mutedBlue', 'selection') },
    ink: { background: TOKENS.espresso, color: TOKENS.ivory, border: 'none' },
    jp: { background: alpha('japanAccent', 'tint'), color: TOKENS.japanAccent, border: '1px solid ' + alpha('japanAccent', 'selection') },
    zh: { background: alpha('chinaAccent', 'tint'), color: TOKENS.chinaAccent, border: '1px solid ' + alpha('chinaAccent', 'selection') },
    subtle: { background: TOKENS.warmGrey, color: TOKENS.walnut, border: '1px solid ' + TOKENS.border },
  };
  const s = styles[variant] || styles.terracotta;
  return (
    <span style={{ ...s, borderRadius: 9999, padding: '3px 10px', fontSize: type.badge, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: 0.3, display: 'inline-block', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────

// `type` passes through: leave it out and the browser default applies, as it always has.
// Any other prop (aria-label, tabIndex, aria-hidden) lands on the <button> as given.
export function Btn({ children, variant = 'primary', onClick, style = {}, size = 'md', disabled = false, type: buttonType, ...rest }) {
  const [hov, setHov] = React.useState(false);
  const sizes = { sm: { padding: '8px 20px', fontSize: type.body }, md: { padding: '12px 28px', fontSize: 15 }, lg: { padding: '14px 32px', fontSize: type.lead } };
  const variants = {
    primary: { background: hov ? TOKENS.terracottaDark : TOKENS.terracotta, color: TOKENS.white, border: 'none' },
    ghost: { background: hov ? alpha('terracotta', 'hover') : 'transparent', color: TOKENS.terracotta, border: '2px solid ' + TOKENS.terracotta },
    ink: { background: hov ? TOKENS.walnut : TOKENS.espresso, color: TOKENS.ivory, border: 'none' },
    white: { background: hov ? alpha('terracotta', 'hover') : TOKENS.white, color: TOKENS.terracotta, border: '2px solid ' + TOKENS.terracotta },
    subtle: { background: hov ? TOKENS.warmGrey : TOKENS.ivory, color: TOKENS.walnut, border: '1px solid ' + TOKENS.border },
    gold: { background: hov ? TOKENS.caramelDark : TOKENS.caramelGold, color: TOKENS.espresso, border: 'none' },
  };
  const v = variants[variant] || variants.primary;
  const isPrimary = variant === 'primary';
  return (
    <button
      {...rest}
      type={buttonType}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...v, ...sizes[size], borderRadius: 9999, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', outline: 'none',
        transform: hov && !disabled ? 'translateY(-1px)' : 'none',
        boxShadow: hov && !disabled && isPrimary ? '0 6px 20px ' + alpha('terracotta', 'shadow') : 'none',
        opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, ...style
      }}>
      {children}
    </button>
  );
}

// ── Languages ─────────────────────────────────────────────────────────────────
// Single source of truth for the `instructor.language` code (used by the editor dropdown,
// the marketplace badges, and the language filter). Add a language here and it flows everywhere.

export const LANGUAGE_META = {
  JP: { label: 'Japanese', short: '日本語', flag: '🇯🇵', variant: 'jp' },
  ZH: { label: 'Mandarin', short: '中文', flag: '🇨🇳', variant: 'zh' },
  KR: { label: 'Korean', short: '한국어', flag: '🇰🇷', variant: 'terracotta' },
  EN: { label: 'English', short: 'English', flag: '🇬🇧', variant: 'blue' },
  ES: { label: 'Spanish', short: 'Español', flag: '🇪🇸', variant: 'gold' },
  FR: { label: 'French', short: 'Français', flag: '🇫🇷', variant: 'blue' },
};

export function languageMeta(code) {
  return LANGUAGE_META[code] || { label: code || 'Language', short: code || '—', flag: '', variant: 'subtle' };
}

// ── InstructorCard ────────────────────────────────────────────────────────────

// The keys that open a focused card, as they open a native link or button (DS-4).
const CARD_OPEN_KEYS = ['Enter', ' '];

// The card is the one keyboard target (DS-4): a link named "View profile: {name}" that Enter or
// Space opens. The "View profile →" pill inside only draws the affordance, so it is plain text,
// not a control. A card is a link only when it opens something: `interactive` defaults to whether
// an onClick was given. A preview (no onClick, or interactive={false}) and a coming-soon card are
// no target at all.
export function InstructorCard({ instructor, onClick, compact = false, interactive = Boolean(onClick) }) {
  const [hov, setHov] = React.useState(false);
  const isComingSoon = instructor.status === 'coming_soon';
  const opens = interactive && !isComingSoon;
  const target = !opens ? {} : {
    role: 'link',
    tabIndex: 0,
    'aria-label': `View profile: ${instructor.displayName}`,
    onClick,
    onKeyDown: (e) => {
      if (!CARD_OPEN_KEYS.includes(e.key)) return;
      e.preventDefault(); // Space would scroll the page
      onClick?.(e);
    },
  };

  return (
    <div
      data-testid="instructor-card"
      {...target}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: TOKENS.white, borderRadius: 16, border: '1px solid ' + TOKENS.border, overflow: 'hidden',
        cursor: opens ? 'pointer' : 'default', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        transform: hov && !isComingSoon ? 'translateY(-2px)' : 'none',
        boxShadow: hov && !isComingSoon ? '0 8px 24px ' + alpha('espresso', 'shadow') : '0 2px 12px ' + alpha('espresso', 'shadow'),
        opacity: isComingSoon ? 0.7 : 1,
        position: 'relative',
        // A column that fills its grid cell, so every card in a row is the same height and the
        // price/button row can be pinned to the bottom of each one (TTT-81).
        display: 'flex', flexDirection: 'column', height: '100%',
      }}>
      {/* Photo — a fixed height, not an aspect ratio. `aspectRatio` plus `maxHeight` transfers the
          height cap into a width cap (280 × 4/5 = 224px), which left the photo narrower than its
          own card and dragged the Verified badge in with it. */}
      <div data-testid="instructor-card-photo" style={{ position: 'relative', width: '100%', height: compact ? 220 : 280, flexShrink: 0, overflow: 'hidden', background: TOKENS.warmGrey }}>
        {instructor.photo ? (
          <img src={assetUrl(instructor.photo)} alt={instructor.displayName}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.4s', transform: hov ? 'scale(1.03)' : 'scale(1)' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: TOKENS.warmGrey }}>
            <Avatar name={instructor.displayName} size={compact ? 88 : 112} />
          </div>
        )}
        {instructor.badge && (
          <div style={{ position: 'absolute', bottom: 10, left: 10 }}>
            <Badge variant={languageMeta(instructor.language).variant}>{instructor.badge}</Badge>
          </div>
        )}
        {instructor.verified && (
          <div style={{ position: 'absolute', top: 10, right: 10 }}>
            <Badge variant="sage">✓ Verified</Badge>
          </div>
        )}
        {isComingSoon && (
          <div style={{ position: 'absolute', inset: 0, background: alpha('ivory', 'scrim'), backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge variant="gold">Coming soon</Badge>
          </div>
        )}
      </div>
      {/* Body */}
      <div style={{ padding: compact ? '12px 14px' : '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: compact ? type.lead : type.h4, color: TOKENS.espresso }}>{instructor.displayName}</span>
          <span style={{ fontSize: type.lead }}>{instructor.flag}</span>
          {!isComingSoon && <span style={{ marginLeft: 'auto' }}><Badge variant={languageMeta(instructor.language).variant}>{languageMeta(instructor.language).short}</Badge></span>}
        </div>
        {!compact && !isComingSoon && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
            <Stars rating={instructor.rating} size={12} />
            <span style={{ fontSize: type.label, color: TOKENS.driftwood, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{instructor.rating} ({instructor.reviewCount})</span>
          </div>
        )}
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: TOKENS.walnut, margin: '0 0 8px', lineHeight: 1.4 }}>{instructor.tagline}</p>
        {!isComingSoon && !compact && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {(instructor.specialties || []).slice(0, 3).map(s => <Badge key={s} variant="subtle">{s}</Badge>)}
          </div>
        )}
        {/* `marginTop: auto` pins this row to the bottom of the card, so a row of cards lines up
            however long each bio and tag list happens to be. */}
        {isComingSoon ? (
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontStyle: 'italic', color: TOKENS.driftwood, marginTop: 'auto', marginBottom: 0 }}>Joining soon</p>
        ) : (
          <div data-testid="instructor-card-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 'auto' }}>
            {/* The cheapest seat a student can actually book, not the instructor's typed profile
                rate (TTT-64). No price means nothing is scheduled yet — say so rather than ₱0.
                `minWidth: 0` lets this wrap instead of squeezing the button beside it. */}
            {typeof instructor.minSessionPrice === 'number' ? (
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: TOKENS.espresso, minWidth: 0 }}>From ₱{instructor.minSessionPrice.toLocaleString()} / session</span>
            ) : (
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, fontStyle: 'italic', color: TOKENS.driftwood, minWidth: 0 }}>No upcoming sessions</span>
            )}
            {/* Drawn like a small ghost Btn, but plain text: the card is the control (DS-4). */}
            <span aria-hidden="true" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 9999,
              border: '2px solid ' + TOKENS.terracotta, color: TOKENS.terracotta,
              background: hov && opens ? alpha('terracotta', 'hover') : 'transparent', transition: 'all 0.2s ease',
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, lineHeight: 'normal',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>View profile →</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TestimonialCard ───────────────────────────────────────────────────────────

export function TestimonialCard({ t, variant = 'cream' }) {
  return (
    <div style={{
      background: variant === 'terracotta' ? alpha('terracotta', 'hover') : TOKENS.white,
      borderRadius: 16, border: '1px solid ' + TOKENS.border,
      padding: '24px 24px 20px', position: 'relative',
      boxShadow: '0 2px 12px ' + alpha('espresso', 'shadow')
    }}>
      <p style={{ fontFamily: "'Lora', serif", fontSize: type.lead, color: TOKENS.espresso, lineHeight: 1.7, fontStyle: 'italic', margin: '0 0 16px' }}>
        "{t.text}"
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Stars rating={t.rating} size={13} />
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.walnut, margin: '4px 0 0', fontWeight: 600 }}>
            — {t.name} {t.flag}
          </p>
        </div>
        <span style={{ fontSize: type.caption, color: TOKENS.driftwood, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t.country}</span>
      </div>
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────

export function Nav({ page, setPage, authState, onLogout, currency, setCurrency }) {
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [authOpen, setAuthOpen] = React.useState(false);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Find Instructors', page: 'marketplace' },
    { label: 'How it works', page: 'how-it-works' },
    { label: 'Blog', page: 'blog' },
  ];

  const authLabel = authState === 'student' ? 'Student' : authState === 'instructor' ? 'Instructor' : authState === 'admin' ? 'Admin' : null;

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: TOKENS.white,
        backdropFilter: 'blur(12px)',
        borderBottom: scrolled ? '1px solid ' + TOKENS.border : '1px solid transparent',
        transition: 'all 0.3s', padding: isMobile ? '0 16px' : '0 32px',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', height: 64, gap: isMobile ? 8 : 24 }}>
          {/* Logo */}
          <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: 0, flexShrink: 0 }}>
            <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? type.h3 : 22, color: TOKENS.terracotta, letterSpacing: -0.5 }}>Ropida</span>
          </button>

          {/* Nav links — desktop only */}
          <div style={{ display: 'flex', gap: 4, flex: 1, justifyContent: 'center' }} className="desktop-nav">
            {navLinks.map(l => (
              <button key={l.page} onClick={() => setPage(l.page)}
                style={{
                  border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, fontSize: type.body,
                  color: page === l.page ? TOKENS.terracotta : TOKENS.espresso, padding: '6px 14px', borderRadius: 9999,
                  background: page === l.page ? alpha('terracotta', 'hover') : 'transparent', transition: 'all 0.15s'
                }}>
                {l.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, marginLeft: 'auto', flexShrink: 0 }}>
            {/* Auth */}
            {authState !== 'loggedout' ? (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setAuthOpen(!authOpen)}
                  style={{ background: alpha('terracotta', 'tint'), border: '1px solid ' + alpha('terracotta', 'selection'), borderRadius: 9999, padding: '6px 12px', fontSize: type.body, fontFamily: "'Plus Jakarta Sans', sans-serif", cursor: 'pointer', color: TOKENS.espresso, fontWeight: 600 }}>
                  {authLabel} ▾
                </button>
                {authOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: TOKENS.white, border: '1px solid ' + TOKENS.border, borderRadius: 14, overflow: 'hidden', boxShadow: '0 8px 24px ' + alpha('espresso', 'shadow'), minWidth: 180, zIndex: 100 }}>
                    <button onClick={() => { setPage(authState === 'student' ? 'dashboard-student' : authState === 'admin' ? 'admin' : 'dashboard-instructor'); setAuthOpen(false); }}
                      style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.espresso }}>
                      {authState === 'student' ? 'My Dashboard' : authState === 'admin' ? 'Admin Dashboard' : 'Instructor Dashboard'}
                    </button>
                    <div style={{ borderTop: '1px solid ' + TOKENS.border }} />
                    <button onClick={() => { onLogout?.(); setAuthOpen(false); }}
                      style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.errorRed }}>
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setPage('login')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, fontSize: type.body, color: TOKENS.espresso, padding: '6px 8px' }}>
                  Log in
                </button>
                <Btn onClick={() => setPage('register')} size="sm">Sign up</Btn>
              </div>
            )}

            {/* CTA — hide on very small screens, shown in mobile menu */}
            {!isMobile && <Btn onClick={() => setPage('marketplace')} size="sm">Find an instructor →</Btn>}

            {/* Hamburger */}
            <button onClick={() => setMobileOpen(!mobileOpen)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none', fontSize: 22, color: TOKENS.espresso, padding: 4 }}
              className="hamburger">{mobileOpen ? '✕' : '☰'}</button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(320px, 85vw)',
          zIndex: 1001, background: TOKENS.white, borderLeft: '1px solid ' + TOKENS.border,
          boxShadow: '-8px 0 24px ' + alpha('espresso', 'shadow'), display: 'flex', flexDirection: 'column', padding: '80px 24px 24px', gap: 8
        }}>
          <button onClick={() => setMobileOpen(false)} style={{ position: 'absolute', top: 20, right: 24, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: TOKENS.espresso }}>✕</button>
          {navLinks.map(l => (
            <button key={l.page} onClick={() => { setPage(l.page); setMobileOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.h4, color: page === l.page ? TOKENS.terracotta : TOKENS.espresso, textAlign: 'left', padding: '10px 0' }}>
              {l.label}
            </button>
          ))}
          <div style={{ borderTop: '1px solid ' + TOKENS.border, paddingTop: 16, marginTop: 8 }}>
            <Btn onClick={() => { setPage('marketplace'); setMobileOpen(false); }} size="md">Find an instructor →</Btn>
          </div>
        </div>
      )}

      {mobileOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: alpha('espresso', 'scrim') }} onClick={() => setMobileOpen(false)} />}
    </>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

export function Footer({ setPage }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{ background: TOKENS.warmGrey, borderTop: '1px solid ' + TOKENS.border, padding: isMobile ? '32px 16px 24px' : '48px 32px 32px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 16 : 24, marginBottom: 24 }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: TOKENS.terracotta }}>Ropida</span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.walnut, margin: 0, lineHeight: 1.4 }}>Find your language teacher.</p>
          </div>

          {/* Minimal links */}
          <div style={{ display: 'flex', gap: isMobile ? 12 : 24, flexWrap: 'wrap' }}>
            {[['Find Instructors', 'marketplace'], ['Pricing', 'pricing'], ['How it works', 'how-it-works']].map(([l, p]) => (
              <button key={p} onClick={() => setPage(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.walnut, padding: 0 }}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{ borderTop: '1px solid ' + TOKENS.border, paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: TOKENS.driftwood, margin: 0 }}>© 2026 Ropida. All rights reserved.</p>
          <a href="/welcome/" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.label, color: TOKENS.terracotta, textDecoration: 'none', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true">🌸</span> Explore all Ropida sites <span aria-hidden="true">→</span>
          </a>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: TOKENS.driftwood, margin: 0 }}>がんばって！加油！</p>
        </div>
      </div>
    </footer>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────

// Everything a keyboard can land on. The dialog keeps Tab inside this set (DS-2.2).
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

// The one pop-up of the app (DS-2). It is a modal dialog: named by its title (`labelledBy`, the
// id of its heading) or by `label`; focus moves in on open and goes back to the opener on close;
// Tab and Shift+Tab wrap inside; Escape closes it. Escape stops at this dialog, so a dialog opened
// from another closes alone, and window listeners (the dashboard menu) never see that key.
// `onClose` undefined means the dialog is busy and cannot close. `closeOnBackdrop={false}` keeps
// it open on a stray click outside, for pop-ups where typed input would be lost.
//
// `footer` pins content to the bottom of the card, outside the scrolling area, so an action
// button stays reachable however long the body runs (TTT-83).
export function Modal({ children, onClose, maxWidth = 600, footer = null, labelledBy, label, closeOnBackdrop = true }) {
  const dialogRef = React.useRef(null);
  // Read while rendering: a child with autoFocus takes focus before any effect runs, so an
  // effect would record that child instead of the button that opened the dialog.
  const [opener] = React.useState(() => (typeof document === 'undefined' ? null : document.activeElement));

  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => {
      if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [opener]);

  // After every render: a step that swaps its content (Request booking → the payment step, Submit
  // review → the thank-you) removes the focused button, and focus falls to the page. Escape and
  // Tab only reach this dialog from inside it, so bring focus back in.
  React.useEffect(() => {
    const dialog = dialogRef.current;
    const active = document.activeElement;
    if (dialog && (!active || active === document.body || !dialog.contains(active))) dialog.focus();
  });

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose?.();
      return;
    }
    if (e.key !== 'Tab') return;
    // A dialog inside this one handles its own Tab; this one must not wrap it a second time.
    e.stopPropagation();
    const dialog = dialogRef.current;
    const items = Array.from(dialog.querySelectorAll(FOCUSABLE));
    if (items.length === 0) {
      e.preventDefault();
      dialog.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: alpha('espresso', 'scrim'), backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
      onClick={closeOnBackdrop ? onClose : undefined}>
      {/* `maxHeight`, not `height`: a short modal stays as small as its content. */}
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label}
        tabIndex={-1} onKeyDown={onKeyDown}
        style={{ background: TOKENS.ivory, borderRadius: 20, maxWidth, width: '100%', maxHeight: '90vh', overflow: 'hidden', position: 'relative', boxShadow: '0 24px 80px ' + alpha('espresso', 'shadow'), display: 'flex', flexDirection: 'column', outline: 'none' }}
        onClick={e => e.stopPropagation()}>
        <button type="button" aria-label="Close" onClick={onClose} disabled={!onClose} style={{ position: 'absolute', top: 16, right: 16, background: TOKENS.warmGrey, border: '1px solid ' + TOKENS.border, borderRadius: '50%', width: 32, height: 32, cursor: onClose ? 'pointer' : 'not-allowed', fontSize: type.h4, color: TOKENS.walnut, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}><span aria-hidden="true">✕</span></button>
        {/* `minHeight: 0` is what lets this shrink below its content. Without it a flex child
            refuses to shrink, the card grows past its max height, and nothing ever scrolls. */}
        <div data-testid="modal-body" style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>{children}</div>
        {footer && (
          <div data-testid="modal-footer"
            style={{ flexShrink: 0, background: TOKENS.ivory, borderTop: '1px solid ' + TOKENS.border, padding: '16px 32px', borderRadius: '0 0 20px 20px' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── FAQ Accordion ─────────────────────────────────────────────────────────────

export function FAQ({ items }) {
  const [open, setOpen] = React.useState(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <div key={i} style={{ background: TOKENS.white, borderRadius: 16, border: '1px solid ' + TOKENS.border, overflow: 'hidden', transition: 'all 0.2s' }}>
          <button onClick={() => setOpen(open === i ? null : i)}
            style={{ width: '100%', padding: '16px 20px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', gap: 16 }}>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: TOKENS.espresso }}>{item.q}</span>
            <span style={{ fontSize: type.h4, color: TOKENS.terracotta, flexShrink: 0, transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'none' }}>+</span>
          </button>
          {open === i && (
            <div style={{ padding: '0 20px 16px' }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: TOKENS.walnut, margin: 0, lineHeight: 1.6 }}>{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Section Heading ───────────────────────────────────────────────────────────

export function SectionHeading({ eyebrow, title, subtitle, center = false }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ textAlign: center ? 'center' : 'left', marginBottom: 40 }}>
      {eyebrow && !title && (
        <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 26 : 32, color: TOKENS.espresso, margin: '0 0 10px', lineHeight: 1.15 }}>
          {eyebrow}
        </h2>
      )}
      {eyebrow && title && (
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.label, color: TOKENS.terracotta, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {eyebrow}
        </p>
      )}
      {title && (
        <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 26 : 32, color: TOKENS.espresso, margin: '0 0 10px', lineHeight: 1.15 }}>
          {title}
        </h2>
      )}
      {subtitle && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? type.body : type.lead, color: TOKENS.walnut, margin: 0, lineHeight: 1.6, maxWidth: 560, ...(center ? { margin: '0 auto' } : {}) }}>{subtitle}</p>}
    </div>
  );
}
